import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  InputBase,
  Stack,
  Fade,
  Button,
  Popover,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  useTheme,
} from "@mui/material";
import { AutoAwesome, FiberManualRecord } from "@mui/icons-material";
import editorTokens from "../SignRequestEditor.tokens";
import {
  AssistantTabIcon,
  ChevronDownIcon,
  PaperclipIcon,
  SendArrowIcon,
} from "./EditorIcons";
import RightPanelHeader from "./RightPanelHeader";
import { RequestFile, SignRequest } from "models/interfaces";
import { askPdf, askPdfCallback, askSignedPdf } from "../SignRequestService";
import { apiErrorMessage, openErrorNotification } from "helpers/methods";

interface Message {
  fileName?: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: Date;
  fileResults?: FileResult[];
  suggestedQuestions?: string[];
}

interface FileResult {
  fileName: string;
  answer: string;
  requestId: string;
  suggestedQuestions: string[];
  /** The AI's id for the document; core resolves it from `files[].fileName + requestId`. */
  documentId?: string;
  citations?: {
    documentId: string;
    documentName?: string | null;
    pageNumber?: number | null;
    quote?: string | null;
    label?: string | null;
  }[];
}

const {
  assistant,
  rightRail,
  scrollbar,
  palette: editorPalette,
} = editorTokens;

/** What the assistant says when the failure carries nothing worth repeating. */
const GENERIC_CHAT_ERROR =
  "Sorry, I encountered an error while processing your request. Please try again.";

/** Short document name for the composer placeholder / long form for the intro. */
const describeScope = (
  selected: RequestFile[],
  total: number,
  nameOf: (file: RequestFile) => string,
) => {
  if (selected.length === 1) {
    const name = nameOf(selected[0]);
    return { long: name, short: name };
  }
  if (total > 0 && selected.length === total) {
    return { long: "all documents in this request", short: "all documents" };
  }
  if (selected.length === 0) {
    return { long: "the selected documents", short: "the documents" };
  }
  return {
    long: `${selected.length} selected documents`,
    short: `${selected.length} documents`,
  };
};

const ChatWithPdf = ({
  isPublic,
  requesToken,
  signRequest,
  hideHeader,
  headerAction,
  fillHeight = false,
  starterPrompts,
  signed,
}: {
  isPublic?: boolean;
  requesToken?: string;
  hideHeader?: boolean;
  headerAction?: React.ReactNode;
  /** Completed request: ask the SIGNED copies, which know who signed and when. */
  signed?: boolean;
  signRequest: SignRequest;
  fillHeight?: boolean;
  /**
   * Opening prompts shown before the first question. The sender-side editor
   * passes the set from `Sign Request Editor.dc.html`; the signer view keeps
   * the reader-oriented defaults.
   */
  starterPrompts?: string[];
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef: any = useRef(null);

  const [selectedFiles, setSelectedFiles] = useState<RequestFile[]>([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>(
    starterPrompts ?? [
      "Summarize this document",
      "What are the main themes of the document?",
      "What conclusions does the author make?",
    ],
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openFileMenu = Boolean(anchorEl);

  const totalFiles = signRequest?.files?.length ?? 0;
  /**
   * What this file is called in the flow we are in, and the name the server looks it up by. The editor
   * fills `unsignedDoc`, a completed request fills `signedPdf` -- so reading one alone left the
   * post-sign rows blank and all comparing equal (undefined === undefined), ticking every box at once.
   */
  const nameOf = (file: RequestFile): string =>
    (signed ? file.signedPdf : undefined) ||
    file.unsignedPdf ||
    file.unsignedDoc ||
    "";

  const { long: askScopeLabel, short: askScopeShort } = describeScope(
    selectedFiles,
    totalFiles,
    nameOf,
  );
  const docPickerLabel =
    selectedFiles.length === 0
      ? "Select documents to ask about"
      : selectedFiles.length === totalFiles
        ? `All documents (${totalFiles})`
        : selectedFiles.length === 1
          ? nameOf(selectedFiles[0])
          : `${selectedFiles.length} documents selected`;

  // The design opens scoped to every document in the request ("All documents
  // (2)"), so the picker starts fully selected rather than empty.
  const hasSeededSelection = useRef(false);
  useEffect(() => {
    if (hasSeededSelection.current || !signRequest?.files?.length) return;
    hasSeededSelection.current = true;
    setSelectedFiles(signRequest.files);
  }, [signRequest?.files]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const theme = useTheme();

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleOpenFileMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFileMenu = () => {
    setAnchorEl(null);
  };

  const handleFileToggle = (file: RequestFile) => {
    const name = nameOf(file);
    setSelectedFiles((prev) => {
      const isSelected = prev.some((f) => nameOf(f) === name);
      if (isSelected) {
        return prev.filter((f) => nameOf(f) !== name);
      } else {
        return [...prev, file];
      }
    });
  };

  const isFileSelected = (fileName: string) => {
    return selectedFiles.some((f) => nameOf(f) === fileName);
  };

  const generatePayload = (selectedFiles: RequestFile[], question: string) => {
    const filesPayload = selectedFiles.map((file) => ({
      // Post-sign addresses the signed copy, which the server looks up by its own name.
      fileName: nameOf(file),
      requestId: signRequest.id,
    }));
    return {
      files: filesPayload,
      question: question,
    };
  };

  const handleSendMessage = async (question = inputValue) => {
    if (!question.trim()) return;

    if (selectedFiles.length === 0) {
      openErrorNotification("Please select at least one document");
      return;
    }

    setShowSuggestions(false);

    const userMessage: Message = {
      role: "user",
      content: question,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsTyping(true);

    try {
      const payload = generatePayload(selectedFiles, question);
      const apiResponse = await (signed
        ? askSignedPdf(payload)
        : isPublic
          ? askPdfCallback(payload, requesToken!)
          : askPdf(payload));
      const newSuggestions =
        apiResponse.multiPdfResults?.[0]?.suggestedQuestions || [];

      const aiMessage: Message = {
        role: "assistant",
        content: "",
        timestamp: new Date(),
        fileResults: apiResponse.multiPdfResults,
        suggestedQuestions: newSuggestions,
      };

      setSuggestedQuestions(newSuggestions);
      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      const message = apiErrorMessage(error, GENERIC_CHAT_ERROR);
      openErrorNotification(message);

      const errorMessage: Message = {
        role: "assistant",
        content: message,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSuggestionClick = (question: string) => {
    handleSendMessage(question);
  };

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Fade in={true} timeout={500}>
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.paper",
        }}
      >
        <Stack
          direction="column"
          display="flex"
          justifyContent="space-between"
          sx={fillHeight ? { flex: 1, minHeight: 0 } : undefined}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {!hideHeader && (
              <RightPanelHeader
                icon={<AssistantTabIcon size={rightRail.headerIconSize} />}
                title="AI Assistant"
                headerAction={headerAction}
              />
            )}
            <Box
              // Chat transcript: announce assistant replies as they arrive
              // (WCAG 4.1.3), mirroring the mobile AI sheet's log region.
              role="log"
              aria-live="polite"
              aria-relevant="additions"
              sx={{
                ...(fillHeight
                  ? { flex: 1, minHeight: 0 }
                  : {
                      height: isPublic
                        ? `calc(100vh - ${selectedFiles.length > 0 ? 345 : 300}px)`
                        : `calc(100vh - ${selectedFiles.length > 0 ? 375 : 335}px)`,
                    }),
                overflowY: "auto",
                p:
                  showSuggestions && messages.length === 0
                    ? assistant.padding
                    : "18px 16px",
                backgroundColor: "background.paper",
                display: "flex",
                flexDirection: "column",
                gap:
                  showSuggestions && messages.length === 0
                    ? assistant.gap
                    : assistant.bubbleGap,
                "&::-webkit-scrollbar": { width: scrollbar.size },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: scrollbar.thumb,
                  border: scrollbar.thumbBorder,
                  backgroundClip: "content-box",
                  borderRadius: scrollbar.radius,
                },
              }}
            >
              {showSuggestions && messages.length === 0 ? (
                <>
                  <Typography
                    sx={{
                      fontSize: assistant.introFontSize,
                      color: assistant.introColor,
                      lineHeight: assistant.introLineHeight,
                      mb: assistant.introMarginBottom,
                    }}
                  >
                    Ask anything about {askScopeLabel} before you send.
                  </Typography>
                  {suggestedQuestions.map((question, index) => (
                    <Button
                      key={index}
                      onClick={() => handleSuggestionClick(question)}
                      sx={{
                        "&.MuiButton-root": {
                          background: editorPalette.white,
                          color: assistant.promptColor,
                          textTransform: "none",
                          justifyContent: "flex-start",
                          textAlign: "left",
                          p: assistant.promptPadding,
                          borderRadius: assistant.promptRadius,
                          border: `1px solid ${editorPalette.borderLight}`,
                          fontSize: assistant.promptFontSize,
                          fontWeight: assistant.promptFontWeight,
                          letterSpacing: "normal",
                          lineHeight: assistant.promptLineHeight,
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                          minHeight: "fit-content",
                          "&:hover": {
                            border: `1px solid ${assistant.promptHoverBorder}`,
                            background: assistant.promptHoverBg,
                          },
                        },
                      }}
                    >
                      {question}
                    </Button>
                  ))}
                </>
              ) : (
                <>
                  {messages.map((message, index) => {
                    const showTimestamp =
                      index === 0 ||
                      message.timestamp.getTime() -
                        messages[index - 1].timestamp.getTime() >
                        300000;

                    return (
                      <Box key={index}>
                        {showTimestamp && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              mb: 2,
                              mt: index > 0 ? 1 : 0,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                backgroundColor: "background.paper",
                                px: 2,
                                py: 0.5,
                                borderRadius: 3,
                                fontSize: "0.7rem",
                                color: "text.secondary",
                                border: `1px solid ${theme.palette.signer.border.main}`,
                              }}
                            >
                              {message.timestamp.toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Typography>
                          </Box>
                        )}

                        {message.role === "system" ? (
                          <Box
                            sx={{
                              backgroundColor: theme.palette.signer.orange.tint,
                              p: 1.5,
                              borderRadius: 2,
                              textAlign: "center",
                              border: `1px solid ${theme.palette.signer.orange.border}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {message.content}
                            </Typography>
                          </Box>
                        ) : (
                          <Fade in={true} timeout={300}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent:
                                  message.role === "user"
                                    ? "flex-end"
                                    : "flex-start",
                                mb: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  maxWidth: "85%",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems:
                                    message.role === "user"
                                      ? "flex-end"
                                      : "flex-start",
                                  width: message.fileResults ? "100%" : "auto",
                                }}
                              >
                                {message.role === "assistant" && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      mb: 0.5,
                                      ml: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: "50%",
                                        backgroundColor: "primary.main",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <AutoAwesome
                                        sx={{
                                          color: "common.white",
                                          fontSize: 12,
                                        }}
                                      />
                                    </Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "0.8rem",
                                        fontWeight: 600,
                                        color: "signer.orange.main",
                                      }}
                                    >
                                      AI Assistant
                                    </Typography>
                                  </Box>
                                )}

                                {message.fileResults ? (
                                  <Stack spacing={1.5} sx={{ width: "100%" }}>
                                    {message.fileResults.map((result, idx) => {
                                      const title = `${result.fileName}`;
                                      return (
                                        <Paper
                                          key={idx}
                                          elevation={0}
                                          sx={{
                                            p: 1.5,
                                            backgroundColor: "background.paper",
                                            borderRadius: "16px 16px 16px 4px",
                                            border: `1px solid ${theme.palette.signer.border.main}`,
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                              boxShadow:
                                                "0 2px 6px rgba(0,0,0,0.12)",
                                            },
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontSize: "0.8rem",
                                              fontWeight: 600,
                                              color: theme.palette.text.primary,
                                              display: "block",
                                              mb: 1,
                                              overflowWrap: "break-word",
                                            }}
                                          >
                                            {title}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.8rem",
                                              lineHeight: 1.6,
                                              color: "text.primary",
                                              whiteSpace: "pre-line",
                                              wordBreak: "break-word",
                                            }}
                                          >
                                            {result.answer}
                                          </Typography>
                                        </Paper>
                                      );
                                    })}
                                  </Stack>
                                ) : (
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: assistant.bubblePadding,
                                      backgroundColor:
                                        message.role === "user"
                                          ? editorPalette.accent
                                          : editorPalette.surface,
                                      color:
                                        message.role === "user"
                                          ? editorPalette.white
                                          : editorPalette.ink,
                                      borderRadius:
                                        message.role === "user"
                                          ? assistant.bubbleUserRadius
                                          : assistant.bubbleAiRadius,
                                      border: "none",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        // Inherit the bubble's per-role color
                                        // (white for user, ink for assistant)
                                        // rather than body2's default
                                        // text.primary.
                                        color: "inherit",
                                        whiteSpace: "pre-line",
                                        fontSize: assistant.bubbleFontSize,
                                        lineHeight: assistant.bubbleLineHeight,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {message.content
                                        .split("\n")
                                        .map((line, i) => {
                                          if (
                                            line.trim().startsWith("â˜‘") ||
                                            line.trim().startsWith("â–¡") ||
                                            line.trim().startsWith("â€¢") ||
                                            line.trim().startsWith("-")
                                          ) {
                                            return (
                                              <Box
                                                key={i}
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "flex-start",
                                                  mb: 0.75,
                                                  pl: 0.5,
                                                }}
                                              >
                                                <FiberManualRecord
                                                  sx={{
                                                    fontSize: 6,
                                                    mt: 1,
                                                    mr: 1,
                                                    color:
                                                      message.role === "user"
                                                        ? "common.white"
                                                        : theme.palette.signer
                                                            .orange.main,
                                                  }}
                                                />
                                                <span>
                                                  {line.replace(
                                                    /^[â˜‘â–¡â€¢\-]\s*/,
                                                    "",
                                                  )}
                                                </span>
                                              </Box>
                                            );
                                          }
                                          return (
                                            <span key={i}>
                                              {line}
                                              {i <
                                                message.content.split("\n")
                                                  .length -
                                                  1 && <br />}
                                            </span>
                                          );
                                        })}
                                    </Typography>
                                  </Paper>
                                )}
                              </Box>
                            </Box>
                          </Fade>
                        )}
                        {message?.suggestedQuestions?.length! > 0 &&
                          index === messages.length - 1 && (
                            <Box
                              mt={3}
                              display={"flex"}
                              justifyContent={"center"}
                              alignItems={"center"}
                              flexDirection={"column"}
                            >
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                gutterBottom
                              >
                                Suggested Questions
                              </Typography>
                              <Stack
                                spacing={1.5}
                                sx={{ width: "100%", px: 0 }}
                              >
                                {message?.suggestedQuestions?.map(
                                  (question, index) => (
                                    <Button
                                      key={index}
                                      onClick={() =>
                                        handleSuggestionClick(question)
                                      }
                                      sx={{
                                        backgroundColor: "background.paper",
                                        color: "text.primary",
                                        textTransform: "none",
                                        justifyContent: "flex-start",
                                        textAlign: "left",
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: `1px solid ${theme.palette.signer.border.main}`,
                                        fontSize: "0.75rem",
                                        fontWeight: 500,
                                        display: "flex",
                                        alignItems: "center",
                                        transition: "all 0.2s ease",
                                        whiteSpace: "normal",
                                        wordWrap: "break-word",
                                        minHeight: "fit-content",
                                        "&:hover": {
                                          transform: "translateY(-2px)",
                                        },
                                      }}
                                    >
                                      <AutoAwesome
                                        sx={{
                                          fontSize: 16,
                                          mr: 1.5,
                                          color: "signer.orange.main",
                                          flexShrink: 0,
                                        }}
                                      />
                                      <span style={{ flex: 1 }}>
                                        {question}
                                      </span>
                                    </Button>
                                  ),
                                )}
                              </Stack>
                            </Box>
                          )}
                      </Box>
                    );
                  })}

                  {isTyping && (
                    <Fade in={true} timeout={300}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {/* Visually-hidden so the transcript log announces that
                            the assistant is composing a reply. */}
                        <Box
                          component="span"
                          sx={{
                            position: "absolute",
                            width: "1px",
                            height: "1px",
                            overflow: "hidden",
                            clip: "rect(0 0 0 0)",
                            clipPath: "inset(50%)",
                          }}
                        >
                          AI Assistant is responding
                        </Box>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            backgroundColor: "primary.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <AutoAwesome
                            sx={{ color: "common.white", fontSize: 12 }}
                          />
                        </Box>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            px: 2.5,
                            borderRadius: "16px 16px 16px 4px",
                            border: `1px solid ${theme.palette.signer.border.main}`,
                            backgroundColor: "background.paper",
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor:
                                  theme.palette.primary.main,
                                animation: "bounce 1.4s infinite",
                              }}
                            />
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor:
                                  theme.palette.primary.main,
                                animation: "bounce 1.4s infinite 0.2s",
                              }}
                            />
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor:
                                  theme.palette.primary.main,
                                animation: "bounce 1.4s infinite 0.4s",
                              }}
                            />
                          </Stack>
                        </Paper>
                      </Box>
                    </Fade>
                  )}
                </>
              )}
              <div ref={messagesEndRef} />
            </Box>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              p: rightRail.footerPadding,
              display: "flex",
              flexDirection: "column",
              gap: rightRail.footerGap,
              borderTop: `1px solid ${editorPalette.borderLight}`,
              backgroundColor: "background.paper",
            }}
          >
            <Box>
              <IconButton
                onClick={handleOpenFileMenu}
                disableRipple
                sx={{
                  width: "100%",
                  // No global `border-box` in this app (see the signer button
                  // in SignRequestHelper) — the dashed border and padding would
                  // otherwise overflow the panel.
                  boxSizing: "border-box",
                  height: assistant.pickerHeight,
                  justifyContent: "flex-start",
                  gap: assistant.pickerGap,
                  px: assistant.pickerPaddingX,
                  py: 0,
                  borderRadius: assistant.pickerRadius,
                  border: `${assistant.pickerBorderWidth} dashed ${
                    selectedFiles.length
                      ? editorPalette.accentBorder
                      : editorPalette.borderLight
                  }`,
                  backgroundColor: selectedFiles.length
                    ? editorPalette.accentSoft
                    : editorPalette.white,
                  color: editorPalette.body,
                  ":hover": {
                    backgroundColor: selectedFiles.length
                      ? editorPalette.accentSoft
                      : editorPalette.white,
                    borderColor: editorPalette.accent,
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    flexShrink: 0,
                    color: editorPalette.accent,
                  }}
                >
                  <PaperclipIcon size={assistant.pickerIconSize} />
                </Box>
                <Typography
                  sx={{
                    fontSize: assistant.pickerFontSize,
                    fontWeight: assistant.pickerFontWeight,
                    lineHeight: "normal",
                    flex: 1,
                    textAlign: "left",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {docPickerLabel}
                </Typography>
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    flexShrink: 0,
                    color: editorPalette.muted,
                    transform: openFileMenu ? "rotate(180deg)" : "none",
                    transition: "transform .2s ease",
                  }}
                >
                  <ChevronDownIcon size={assistant.pickerCaretSize} />
                </Box>
              </IconButton>

              <Popover
                open={openFileMenu}
                anchorEl={anchorEl}
                onClose={handleCloseFileMenu}
                // On mobile this chat sits in a bottom sheet above a full-screen dialog, and a Popover
                // defaults to the same zIndex.modal as both -- so the picker opened behind the sheet.
                sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                PaperProps={{
                  sx: {
                    width: 300,
                    maxHeight: 300,
                    mt: 0.5,
                    borderRadius: "12px",
                    border: `1px solid ${editorPalette.borderLight}`,
                    boxShadow: "0 -8px 30px rgba(33,21,71,.14)",
                    p: "6px",
                  },
                }}
              >
                <List sx={{ p: 0 }}>
                  {signRequest.files.map((file: RequestFile) => (
                    <ListItem
                      key={nameOf(file)}
                      button
                      onClick={() => handleFileToggle(file)}
                      sx={{
                        py: "8px",
                        px: "10px",
                        borderRadius: "7px",
                        "&:hover": {
                          backgroundColor: editorPalette.accentTint,
                        },
                      }}
                    >
                      <Checkbox
                        checked={isFileSelected(nameOf(file))}
                        sx={{
                          p: 0,
                          mr: "9px",
                          color: editorPalette.borderMain,
                          "&.Mui-checked": { color: editorPalette.accent },
                          "& .MuiSvgIcon-root": { fontSize: 17 },
                        }}
                      />
                      <ListItemText
                        primary={nameOf(file)}
                        primaryTypographyProps={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: editorPalette.ink,
                          noWrap: true,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Popover>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: assistant.pickerGap,
                minHeight: assistant.composerMinHeight,
                p: assistant.composerPadding,
                border: `${assistant.composerBorderWidth} solid ${editorPalette.borderLight}`,
                borderRadius: assistant.composerRadius,
                backgroundColor: editorPalette.white,
              }}
            >
              <InputBase
                placeholder={`Ask about ${askScopeShort}`}
                value={inputValue}
                onChange={(e: any) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  "& .MuiInputBase-input": {
                    // The design's bare <input> keeps Chrome's UA inset and
                    // `normal` line-height; MUI zeroes the first and forces
                    // 1.4375em on the second.
                    padding: "1px 2px",
                    height: "auto",
                    lineHeight: "normal",
                    fontSize: assistant.composerFontSize,
                    color: editorPalette.ink,
                    "&::placeholder": {
                      color: editorPalette.muted,
                      opacity: 1,
                    },
                  },
                }}
              />
              <IconButton
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim()}
                aria-label="Send question"
                sx={{
                  width: assistant.sendSize,
                  height: assistant.sendSize,
                  flexShrink: 0,
                  borderRadius: assistant.sendRadius,
                  backgroundColor: editorPalette.accent,
                  color: editorPalette.white,
                  "&:hover": { backgroundColor: editorPalette.accentHover },
                  "&.Mui-disabled": {
                    backgroundColor: editorPalette.accentDisabled,
                    color: editorPalette.white,
                  },
                }}
              >
                <SendArrowIcon size={assistant.sendIconSize} />
              </IconButton>
            </Box>
            <Typography
              sx={{
                fontSize: assistant.disclaimerFontSize,
                color: assistant.disclaimerColor,
                lineHeight: assistant.disclaimerLineHeight,
                textAlign: "center",
              }}
            >
              {signed
                ? "AI can make mistakes. Verify important details against the signed document before relying on them."
                : "AI can make mistakes. Verify important details against the document before sending."}
            </Typography>
          </Box>
        </Stack>
        <style>
          {`
            @keyframes bounce {
              0%, 80%, 100% { transform: translateY(0); }
              40% { transform: translateY(-6px); }
            }
          `}
        </style>
      </Paper>
    </Fade>
  );
};

export default ChatWithPdf;
